/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import { ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { LaneManagerService } from '../lane-buttons/shared/services/lane-manager/lane-manager.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { KeyboardCombos } from 'common/common/constants/keyboard-combos';
import { BcKeyboardService } from 'common/keyboard-service/keyboard.service';
import { GlobalKeyboardShortcutsService } from 'common/keyboard-service/global-keyboard-shortcuts.service';
import { TranslateService } from '@ngx-translate/core';
import { TinyMCEPluginsService } from './tinymce-plugins.service';
import { ActivityHandlerService } from 'common/inactivity-handler/activity-handler.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { AccessibilityFontSizeSetterService } from '../accessibility-font-size-setter/accessibility-font-size-setter.service';
import { Subscription } from 'rxjs';
import { AccessibilitySetting } from 'components/navbar/accessibility-settings/accessibility-setting.interface';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcConfigService } from 'common/config/provider';
import { BcCannedMessagesRepo } from 'common/canned-messages/canned-messages-repo';
import { isEqual } from 'lodash';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';

import 'tinymce/icons/default';

const DEFAULT_EDITOR_FONT_SIZE = 14;

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss', './editor-wrapper.scss'],
  encapsulation: ViewEncapsulation.None
})

export class TinyMCEEditorComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input()
  placeholderText: string;

  @Input()
  workItemId: string;

  @Input()
  workItemType: WorkItemTypes;

  @Input()
  editorInitSetting;

  @Input()
  commandList;

  @Input()
  editorDisabled;

  @Input()
  cannedMessageRequest: CannedMessageRequest;

  @Input()
  editorContentModel;

  @Input()
  submitOnEnter: boolean;

  @Input()
  editorMessages;

  @Input()
  showFileUpload: boolean;

  @Input()
  showQuoteButton: boolean;

  @Input()
  isAccessibilitySettingAvailable: boolean;

  @Output()
  editorContentModelChange: EventEmitter<any> = new EventEmitter();

  @Output()
  editorSetupDone: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  onCommandSelected: EventEmitter<any> = new EventEmitter();

  @Output()
  onEditorSubmit: EventEmitter<string> = new EventEmitter();

  @Output()
  change: EventEmitter<any> = new EventEmitter();

  @Output()
  quoteButtonClick: EventEmitter<void> = new EventEmitter<void>();

  public name;
  public imageLink;
  public editorInstance;
  public showInsertImageDialog;
  public showPushLinkDialog;
  public toTextFragment;
  public cannedMessageSubscription;
  public cannedMessageCloseSubscription;
  public smartResponsesSubscription;
  public selectedCommandIndex;
  public isCommandListVisible;
  public commandSelected;
  public pushLink;
  public imageLinkInvalid;
  public filteredBuiltInCommandList;
  public filteredCannedMessageCommandList;
  public allFilteredCommandList;
  public virtualCommandList = [];
  public fontSizeZoomPercentage: string;
  public isFontSizeAccessibilitySettingTurnedOn: boolean;

  private _cannedMessageCommandsList;
  private _lastCannedMessageRequest;
  private _smartAdvisorSubscription: Subscription;
  private _togglePushLinkSubscription: Subscription;
  private _toggleInsertImageSubscription: Subscription;
  private _fontSizePercentageSubscription: Subscription;
  private _accessibilitySubscription: Subscription;
  private _focusSubscription: Subscription;
  private _currentLang: string;

  public initialize;
  public defaultEditorSetting;

  @ViewChild('tinyMceEditor', { static: true }) tinyMceEditor;

  @ViewChild('insertImageInput') insertImageInput;

  @ViewChild('pushLinkInput') pushLinkInput;

  constructor(private _bcAnalytics: BcAnalytics,
              private _rxEmitter: RxEmitter,
              private _cannedMessageRepo: BcCannedMessagesRepo,
              private _globalKeyboardService: GlobalKeyboardShortcutsService,
              private _keyboardService: BcKeyboardService,
              private _changeDetector: ChangeDetectorRef,
              private _translateService: TranslateService,
              private _pluginService: TinyMCEPluginsService,
              private _activityHandlerService: ActivityHandlerService,
              private bcSettingsService: BcSettingsService,
              private _accessibilityFontSizeSetterService: AccessibilityFontSizeSetterService,
              private _bcConfig: BcConfigService,
              private _ngZone: NgZone) {
    this.name = 'bcEditor';

    this._currentLang = this._translateService.currentLang ? this._translateService.currentLang.toLowerCase() : 'en-us';

    this.imageLink = '';
    this.editorInstance = null;
    this.showInsertImageDialog = false;
    this.showPushLinkDialog = false;
    this.toTextFragment = document.createElement('div');
    this._cannedMessageCommandsList = [];

    this.filteredBuiltInCommandList = [];
    this.filteredCannedMessageCommandList = [];
    this.allFilteredCommandList = [];

    this._focusSubscription = this._rxEmitter.subscribe(broadcastEvents.focusEditor, () => {
      this.setFocusToEndOfEditorText();
    });

    this.defaultEditorSetting = {
      branding: false,
      plugins: 'lists advlist link autolink pushLink insertImage',
      default_link_target: '_blank',
      menubar: false,
      statusbar: false,
      toolbar: 'bold italic underline link pushLink insertImage fileUpload',
      forced_root_block: 'p',
      content_css: this._bcConfig.constructAssetUrl('assets/tinymce-style.css'),
      icons: 'bold-tinymce-icons',
      language: this._currentLang,
      language_url: this._bcConfig.constructAssetUrl(`langs/${this._currentLang}.js`),
      theme_url: this._bcConfig.constructAssetUrl('themes/silver/theme.js'),
      skin_url: this._bcConfig.constructAssetUrl('skins/ui/oxide'),
      icons_url: this._bcConfig.constructAssetUrl('icons/bold-tinymce-icons/icons.js'),
      browser_spellcheck: true,
      custom_colors: false,
      contextmenu: false,
      height: '100%',
      setup: (editor) => {
        this.editorInstance = editor;

        this._togglePushLinkSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.TogglePushLinkDialog}${editor.id}`, () => {
          this.togglePushLinkDialog();
        });

        this._toggleInsertImageSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.ToggleInsertImageDialog}${editor.id}`, () => {
          this.toggleImageLinkDialog();
        });

        this.editorSetupDone.emit(editor);

        // this needs to be run out of angular not to start digest loop on every model change
        this.resetCommandListState();

        this.editorInstance.on('keyDown', this.handleKeyDown.bind(this));

        if (this._isAgentLogOutEnabled()) {
          this.bcSettingsService.getInactivityTimeout().then((inactivityTimeout) => {
            if (inactivityTimeout) {
              this._ngZone.runOutsideAngular(() => {
                this.editorInstance.on('keyPress', this._activityHandlerService.resetTimeoutHandler);
              });
            }
          });
        }

        this._pluginService.addDynamicFileUploadButton(editor, this.showFileUpload);

        this.editorInstance.on('init', () => {
          if (this.isAccessibilitySettingAvailable && this.isFontSizeAccessibilitySettingTurnedOn) {
            const fontSizePercentageFromStorage = this._accessibilityFontSizeSetterService.fontSizePercentage;
            this._setFontSize(fontSizePercentageFromStorage);
          }

          // Register global shortcuts to iFrame
          this._globalKeyboardService.registerGlobalKeyboardShortcutsToElement(this.editorInstance.getBody());

          // Register smart response shortcut to iFrame
          this._keyboardService.bindToElement(KeyboardCombos.CtrlSpace, () => {
            this._rxEmitter.send(LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.ToggleSmartResponses, this.workItemId), null);
          }, this.editorInstance.getBody(), { stopEvent: true });
        });
      }
    };
  }

  setFocusToEndOfEditorText() {
    setTimeout(() => {
      this.focusToEditor();
      if (this.editorInstance && this.editorInstance.selection) {
        this.editorInstance.selection.select(this.editorInstance.getBody(), true);
        this.editorInstance.selection.collapse(false);
      }
    });
  }

  focusToEditor() {
    if (this.editorInstance) {
      this.editorInstance.focus();
    }
  }

  ngOnInit(): void {
    this.editorInitSetting = { ...this.defaultEditorSetting, ...this.editorInitSetting };

    this._pluginService.addPushLinkPlugin();
    this._pluginService.addInsertImagePlugin();

    this.tinyMceEditor.initialValue = this.editorContentModel;
    this.tinyMceEditor.init = this.editorInitSetting;

    if (this.isAccessibilitySettingAvailable) {
      this._fontSizePercentageSubscription = this._rxEmitter.subscribe(MessageBusEvents.FontSizeZoom, (zoom) => {
        if (this.isFontSizeAccessibilitySettingTurnedOn) {
          this._setFontSize(zoom);
        }
      });

      this._accessibilitySubscription = this._rxEmitter.subscribe(MessageBusEvents.AccessibilitySetting + bcEnums.accessibilitySettingNames.CONVERSATION_FONT_SIZE, (fontSizeAccessibilitySetting: AccessibilitySetting) => {
        this.isFontSizeAccessibilitySettingTurnedOn = fontSizeAccessibilitySetting.isTurnedOn;
        if (fontSizeAccessibilitySetting.isTurnedOn) {
          this._getAndSetFontSize();
        } else {
          this._resetFontSizeToDefault();
        }
      });
    }

    this.cannedMessageSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.SendCannedMessageToEditor}${this.workItemId}`,
      (cannedMessage) => {
        if (cannedMessage && cannedMessage.text) {
          this.insertHTMLAndFocus(cannedMessage.text);
        }
      });

    this._smartAdvisorSubscription = this._rxEmitter.subscribe(LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.SendCannedMessageToEditor, this.workItemId), (message: string) => this.insertHTMLAndFocus(message));

    this.cannedMessageCloseSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItemId}`, () => {
      setTimeout(() => {
        this.focusToEditor();
      });
    });

    this.smartResponsesSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SendSmartResponseToEditor}${this.workItemId}`,
      (smartResponse) => {
        if (smartResponse && smartResponse.text) {
          this.replaceHTML(smartResponse.text);
        }
      });

    if (this.cannedMessageRequest) {
      this._updateCannedMessageCommandList();
    }

    this.isFontSizeAccessibilitySettingTurnedOn = this._accessibilityFontSizeSetterService.isFontSizeAccessibilitySettingTurnedOn;
  }

  ngOnChanges(change: SimpleChanges) {
    if (this.editorInstance && change.showFileUpload) {
      const isFileUploadDisabled: boolean = !change.showFileUpload.currentValue;

      if (change.showFileUpload.isFirstChange) {
        this.editorInstance.on('fileUploadButtonSetupReady', () => {
          this._pluginService.setFileUploadButtonDisabled(this.editorInstance, isFileUploadDisabled);
        });
      }

      this._pluginService.setFileUploadButtonDisabled(this.editorInstance, isFileUploadDisabled);
    }
  }

  ngOnDestroy() {
    if (this.cannedMessageSubscription) {
      this.cannedMessageSubscription.unsubscribe();
    }

    if (this.smartResponsesSubscription) {
      this.smartResponsesSubscription.unsubscribe();
    }

    if (this.cannedMessageCloseSubscription) {
      this.cannedMessageCloseSubscription.unsubscribe();
    }

    if (this._smartAdvisorSubscription) {
      this._smartAdvisorSubscription.unsubscribe();
    }

    if (this._togglePushLinkSubscription) {
      this._togglePushLinkSubscription.unsubscribe();
    }

    if (this._toggleInsertImageSubscription) {
      this._toggleInsertImageSubscription.unsubscribe();
    }

    if (this._focusSubscription) {
      this._focusSubscription.unsubscribe();
    }

    if (this._fontSizePercentageSubscription) {
      this._fontSizePercentageSubscription.unsubscribe();
    }

    if (this._accessibilitySubscription) {
      this._accessibilitySubscription.unsubscribe();
    }

    if (this.tinyMceEditor) {
      this.tinyMceEditor.init = null;
    }

    if (this.editorInstance) {
      try {
        this.editorInstance.off('init');
        this.editorInstance.off('keyDown');
        if (this._isAgentLogOutEnabled()) {
          this.editorInstance.off('keyPress');
        }
      } catch (e) {
        // Catch tinymce error
      }
      try {
        this.editorInstance.destroy();
        this.editorInstance.remove();
      } catch (e) {
        // Catch tinymce error
      }
      this.editorInstance = null;
    }

    // Release EventEmitters
    this.quoteButtonClick = null;
    this.change = null;
    this.onEditorSubmit = null;
    this.onCommandSelected = null;
    this.onEditorSubmit = null;
    this.editorSetupDone = null;
    this.editorContentModelChange = null;
  }

  ngDoCheck() {
    if (!isEqual(this._lastCannedMessageRequest, this.cannedMessageRequest)) {
      this._updateCannedMessageCommandList();
      this._lastCannedMessageRequest = { ...this.cannedMessageRequest };
    }
  }

  _updateCannedMessageCommandList() {
    this._cannedMessageRepo.getCannedMessages(this.cannedMessageRequest)
      .then(response => {
        this._cannedMessageCommandsList = response.messages
          .filter(message => message.slashCommand)
          .map(message => {
            return {
              shortcut: '/' + message.slashCommand,
              description: message.text,
              cannedMessage: true,
              id: message.id,
              name: message.name,
              text: message.text,
              keywords: message.keywords,
              slashCommand: message.slashCommand,
              type: message.type,
              isVisible: message.isVisible,
              folderId: message.folderId,
              chatType: message.chatType
            };
          });
        this.filterCommandList();
        this._changeDetector.detectChanges();
      });
  }

  resetCommandListState() {
    this.setCommandSelectionStatus(false);
    this.filterCommandList();
    this.selectedCommandIndex = 0;
    this._changeDetector.detectChanges();
  }

  commandListVirtualListUpdated(newList) {
    this.virtualCommandList = newList;
    this._changeDetector.detectChanges();
  }

  getAllFilteredCommandList() {
    let commandArray = [];
    if (this.filteredBuiltInCommandList && this.filteredBuiltInCommandList.length) {
      commandArray.push({
        header: true,
        defaultText: 'bcEditor.availableCommands',
        filteredText: 'bcEditor.commandsMatching'
      });
      commandArray = commandArray.concat(this.filteredBuiltInCommandList);
    }
    if (this.filteredCannedMessageCommandList && this.filteredCannedMessageCommandList.length) {
      commandArray.push({
        header: true,
        defaultText: 'bcEditor.availableCannedMessageCommands',
        filteredText: 'bcEditor.availableCannedMessageCommandsMatching'
      });
      commandArray = commandArray.concat(this.filteredCannedMessageCommandList);
    }
    return commandArray;
  }

  getCommandIndex(command): number {
    return this.filteredBuiltInCommandList.concat(this.filteredCannedMessageCommandList).indexOf(command);
  }

  getEditorText() {
    if (this.editorInstance) {
      return this.editorInstance.getContent({ format: 'text' });
    }
  }

  getEditorTextTillFirstSpace() {
    return this.getEditorText().split(/\s/, 1)[0];
  }

  handleInnerChange(event) {
    if (this.editorContentModel !== event) {
      this.editorContentModel = event;
      this.editorContentModelChange.emit(event);
      // Call bound change listener
      if (this.change) {
        this.change.emit(event);
      }
      this.resetCommandListState();
    }
  }

  handleKeyDown(event) {
    if (this.isCommandListVisible) {
      this.commandListNavigation(event);
    } else {
      if (event.keyCode === KeyboardKeyCodes.ENTER && !event.shiftKey && this.submitOnEnter) {
        event.preventDefault();
        event.stopPropagation();
        this.convertToHyperlink();
        this.onEditorSubmit.emit(this.editorInstance.getContent());
      }
    }
  }

  convertToHyperlink() {
    // If the content is only a URL, convert it to a hyperlink
    const editorText = this.editorInstance.getContent({ format: 'text'});
    const regex = /^(https?:\/\/(www\.)?|www\.)[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)(\n)?$/g;
    const url = regex.exec(editorText);
    if (url?.length > 0) {
      this.editorInstance.selection.select(this.editorInstance.getBody(), true);
      this.editorInstance.execCommand('createLink', false, url[0].trim());
    }
  }

  commandListNavigation(event) {
    if (this.isCommandListVisible) {
      switch (event.which) {
        case KeyboardKeyCodes.UP:
          event.preventDefault();
          event.stopPropagation();
          this.setSelectedCommandIndex(this.selectedCommandIndex - 1);
          break;
        case KeyboardKeyCodes.DOWN:
          event.preventDefault();
          event.stopPropagation();
          this.setSelectedCommandIndex(this.selectedCommandIndex + 1);
          break;
        case KeyboardKeyCodes.TAB:
        case KeyboardKeyCodes.ENTER:
          event.preventDefault();
          event.stopPropagation();
          this.selectCommand();
          break;
        case KeyboardKeyCodes.ESC:
          event.preventDefault();
          event.stopPropagation();
          this.setCommandSelectionStatus(true);
          break;
        default:
      }
    }
  }

  onFocus() {
    this._rxEmitter.send(broadcastEvents.editorHasFocus, event);
  }

  calculateCommandListVisibility() {
    if (this.editorInstance) {
      this.isCommandListVisible = this.getEditorText().indexOf('/') === 0 && !this.commandSelected
        && (this.commandList && this.commandList.length > 0
          || this._cannedMessageCommandsList && this._cannedMessageCommandsList.length > 0);
    }
  }

  setSelectedCommandIndex(idx) {
    if (idx === -1) {
      return;
    }
    const previousSelectedIdx = this.selectedCommandIndex;
    this.selectedCommandIndex = Math.max(0, Math.min(idx, this.filteredBuiltInCommandList.length + this.filteredCannedMessageCommandList.length - 1));
    const newSelectedIdx = this.selectedCommandIndex;
    this._changeDetector.detectChanges();

    const selectedCommand: HTMLElement = document.querySelector('.bc-command-list .selected');
    const scrollElement: HTMLElement = document.querySelector('.bc-command-list');
    if (selectedCommand) {
      const isScrollingUp: boolean = (previousSelectedIdx - newSelectedIdx > 0);
      if (isScrollingUp && selectedCommand.getBoundingClientRect().top <= scrollElement.getBoundingClientRect().top) {
        selectedCommand.scrollIntoView();
      } else if (!isScrollingUp) {
        const offsetBottom = selectedCommand.getBoundingClientRect().bottom;
        const scrollBottom = scrollElement.getBoundingClientRect().bottom;
        if (offsetBottom > scrollBottom) {
          selectedCommand.scrollIntoView(false);
        }
      }
      this._changeDetector.detectChanges();
    }
  }

  commandClicked(command) {
    if (!command.header) {
      this.setSelectedCommandIndex(this.getCommandIndex(command));
      this.selectCommand();
    }
  }

  selectCommand() {
    let command = '';
    if (this.selectedCommandIndex < this.filteredBuiltInCommandList.length) {
      command = this.filteredBuiltInCommandList[this.selectedCommandIndex].shortcut;
      this.replaceHTML('');
      this.onCommandSelected.emit(command);
    } else if (this.selectedCommandIndex - this.filteredBuiltInCommandList.length < this.filteredCannedMessageCommandList.length) {
      const selectedCommand = this.filteredCannedMessageCommandList[this.selectedCommandIndex - this.filteredBuiltInCommandList.length];
      this.replaceHTML('');
      setTimeout(() => {
        this._rxEmitter.send(`${MessageBusEvents.SendCannedMessageToEditor}${this.workItemId}`, selectedCommand);
        this._bcAnalytics.eventTrack('CannedMessageSlashCommand', { type: this.cannedMessageRequest.type, command: selectedCommand.shortcut });
      });
    } else {
      command = this.getEditorText();
      this.replaceHTML('');
      this.onCommandSelected.emit(command);
    }
    this.setCommandSelectionStatus(true);
  }

  setCommandSelectionStatus(selected) {
    this.commandSelected = selected;
    this.calculateCommandListVisibility();
  }

  toggleImageLinkDialog() {
    this.showInsertImageDialog = !this.showInsertImageDialog;

    if (this.showInsertImageDialog) {
      this._changeDetector.detectChanges();
      this.insertImageInput.nativeElement.focus();
    } else {
      this.focusToEditor();
    }
  }

  togglePushLinkDialog() {
    this.showPushLinkDialog = !this.showPushLinkDialog;

    if (this.showPushLinkDialog) {
      this._changeDetector.detectChanges();
      this.pushLinkInput.nativeElement.focus();
    } else {
      this.focusToEditor();
    }
  }

  onPushLinkKeyDown(event, form) {
    if (!event) {
      return;
    }

    switch (event.keyCode) {
      case KeyboardKeyCodes.ENTER:
        if (form.form.valid) {
          this.insertPushLink();

          event.preventDefault();
          event.stopPropagation();
        }
        break;
      case KeyboardKeyCodes.ESC:
        this._resetPushLinkDialogAndCloseIt();

        event.preventDefault();
        event.stopPropagation();
        return;
      default:
        return event;
    }
  }

  _resetPushLinkDialogAndCloseIt() {
    this.pushLink = null;
    this.showPushLinkDialog = false;
    this.focusToEditor();
  }

  insertPushLink() {
    let link = this.pushLink;
    link = this.addHttpIfNecessary(link);

    this.insertHTMLAndFocus(`[${link}]`);

    this._resetPushLinkDialogAndCloseIt();

    this._bcAnalytics.eventTrack('ChatPushLink');
  }

  insertImageLink(event) {
    const context = this;

    function resetAndClose() {
      context.imageLink = '';
      context.showInsertImageDialog = false;
      context.editorInstance.focus();
    }

    // TODO: We have to revisit this keyboard hack after this PR https://github.com/basecamp/trix/pull/143 is merged
    if (event && event.keyCode) {
      switch (event.keyCode) {
        case KeyboardKeyCodes.ENTER:
          event.preventDefault();
          event.stopPropagation();
          break;
        case KeyboardKeyCodes.ESC:
          resetAndClose();
          event.preventDefault();
          event.stopPropagation();
          return;
        default:
          event.stopPropagation();
          return event;
      }
    }

    // "src" is the name of the input field inside the form
    // "<form><input type="url" name="src" /></form>"
    if (event.target.form.src.validity.valid && event.target.form.checkValidity()) {
      this.imageLinkInvalid = false;

      const image = `<img src="${this.imageLink}">`;
      this.insertHTMLAndFocus(image);

      resetAndClose();
    } else if (!event.target.form.src.validity.valid) {
      this.imageLinkInvalid = true;

      return event;
    }
  }

  activateLinksAutomatically() {
    if (!this.editorInstance) {
      return;
    }
    const fullText = this.editorInstance.getContent();
    const selectedRange = this.editorInstance.selection.getRng();
    const textUntilSelectedRange = fullText.substring(0, selectedRange.startOffset);
    if (!textUntilSelectedRange) {
      return;
    }
    const lastWordBeforeSelectedRange = getLastWordBeforeSelectedRange();

    if (!isLastWordUrl()) {
      return;
    }
    const lastWordWithoutPunctuation = slicePunctuationFromLastWord();
    const url = this.addHttpIfNecessary(lastWordWithoutPunctuation);
    activateLink.call(this);

    function getLastWordBeforeSelectedRange() {
      const words = textUntilSelectedRange.split(/\s|\n/);
      return words[words.length - 1] || words[words.length - 2];
    }

    function isLastWordUrl() {
      return lastWordBeforeSelectedRange.match(/^(http:\/\/|https:\/\/|www\.).*$/i);
    }

    function slicePunctuationFromLastWord() {
      if (lastWordBeforeSelectedRange.endsWith('...')) {
        return lastWordBeforeSelectedRange.slice(0, -3);
      } else if (lastWordBeforeSelectedRange.substr(lastWordBeforeSelectedRange.length - 1).match(/([\?\!\.])/)) {
        return lastWordBeforeSelectedRange.slice(0, -1);
      }
      return lastWordBeforeSelectedRange;
    }

    function activateLink() {
      const firstCharPositionInLastWord = textUntilSelectedRange.lastIndexOf(lastWordWithoutPunctuation);
      this.editorInstance.selection.setRng([firstCharPositionInLastWord, firstCharPositionInLastWord + lastWordWithoutPunctuation.length]);

      if (!this.editorInstance.attributeIsActive('href')) {
        this.editorInstance.activateAttribute('href', url);
      }

      this.editorInstance.selection.setRng([selectedRange.startOffset, selectedRange.endOffset]);
    }
  }

  addHttpIfNecessary(link) {
    if (!/^https?:\/\//i.test(link)) {
      return `http://${link}`;
    }
    return link;
  }

  replaceHTML(data) {
    this.editorInstance.setContent(data);
    this.editorContentModelChange.emit(data);
  }

  addHTML(data) {
    if (this.editorInstance) {
      data = data.replace(/( size=\"2\")|( face=\"verdana\")/gmi, '');

      this.editorInstance.insertContent(data);
    }
  }

  insertHTMLAndFocus(data) {
    this.addHTML(data);
  }

  filterCommandList() {
    if (this.isCommandListVisible) {
      const search = this.getEditorTextTillFirstSpace();
      if (this.commandList) {
        this.filteredBuiltInCommandList = this.commandList.filter(command => command.shortcut.indexOf(search) === 0)
          .sort((a, b) => a.shortcut.localeCompare(b.shortcut));
      } else {
        this.filteredBuiltInCommandList = [];
      }
      this.filteredCannedMessageCommandList = this._cannedMessageCommandsList.filter(
        cannedMessageCommand => cannedMessageCommand.shortcut.indexOf(search) === 0
      ).sort((a, b) => a.shortcut.localeCompare(b.shortcut));
      this.allFilteredCommandList = this.getAllFilteredCommandList();
    }
  }

  public onQuoteButtonClick() {
    this.quoteButtonClick.emit();
  }

  public getAccessibilityClass(): string {
    if (this.isFontSizeAccessibilitySettingTurnedOn && this.fontSizeZoomPercentage) {
      return 'zoom-' + this.fontSizeZoomPercentage;
    }
    return;
  }

  private _setFontSize(zoom): void {
    this.fontSizeZoomPercentage = zoom;
    const newFontSize = (DEFAULT_EDITOR_FONT_SIZE * zoom) / 100;
    this.editorInstance.getBody().style.fontSize = newFontSize.toString() + 'px';
  }

  private _getAndSetFontSize(): void {
    const newFontSize = this._accessibilityFontSizeSetterService.fontSizePercentage;
    this._setFontSize(newFontSize);
  }

  private _resetFontSizeToDefault(): void {
    if (this.editorInstance.getBody()) {
      this.editorInstance.getBody().style.fontSize = DEFAULT_EDITOR_FONT_SIZE + 'px';
    }
  }

  private _isAgentLogOutEnabled(): boolean {
    return this._bcConfig.current.features.agentAutoLogout;
  }
}
